import React from 'react';
import omit from 'lodash/omit';
import idx from 'idx';
import { Form, submit } from 'redux-form';
import PageTransition from 'react-router-page-transition';
import PAPQuestionBase from 'pap/components/common/pap_question_base';
import PAPBase from 'pap/containers/common/pap_base';
import { getFieldPropsFromDefinition, getCurrentStepIndex } from 'pap/utils';
import { getProjectUrl } from 'project/utils';
import { getSubcategorySlug } from 'common/ducks/categories';

/**
 * A component responsible for rendering all brief question screens.
 *
 * It's similar to the PAPDetails component, but requires a project draft, which
 * is supposed to contain the list of brief questions in the `brief_questions` attribute.
 *
 * @author Kamil Grymuza <kamil@startupfoundry.com>
 */
class PAPBrief extends React.Component {

  redirectIfNeeded(project) {
    const { subCategory, router } = this.props;
    /* redirect to project page if not on draft status */
    if (project.status !== 'draft') {
      const url = getProjectUrl(project, subCategory.slug);
      router.push(url);
    }
  }

  componentDidMount() {
    const { toggleAgencyQuestion, user } = this.props;

    this.props.fetchCategoriesIfNeeded().then(() => {
      this.props.fetchProjectIfNeeded({projectId: this.props.projectID}).then(({data}) => {
        this.props.fetchPricing(data.sub_category);
        this.redirectIfNeeded(data);
      });
    });
    this.props.fetchAttachments(this.props.projectID);

    toggleAgencyQuestion(!user.is_agency_question_answered);
  }

  currentStepIndex() {
    return getCurrentStepIndex(this.props.briefQuestions, this.props.stepName);
  }

  getNextStepLink() {
    if (this.currentStepIndex() >= this.props.briefQuestions.length - 1) {
      return `/start-a-project/${this.props.projectID}/review/`;
    } else {
      var stem = this.props.briefQuestions[this.currentStepIndex() + 1].name;
      return `/start-a-project/${this.props.projectID}/brief/${stem}/`;
    }
  }

  getPrevStepLink() {
    /* @TODO: handle going back to details section */
    if (this.currentStepIndex() > 0) {
      var stem = this.props.briefQuestions[this.currentStepIndex() - 1].name;
      return `/start-a-project/${this.props.projectID}/brief/${stem}`;
    }
  }

  renderWizardStep() {
    const { briefQuestions, stepName } = this.props;

    const fieldDefinition = briefQuestions.find(q => q.name === stepName);

    if (!fieldDefinition) {
      return null
    }

    const fieldProps = getFieldPropsFromDefinition(fieldDefinition, {
      displayFullScreen: this.props.displayFullScreen,
      hideFullScreen: this.props.hideFullScreen,
      fullScreen: this.props.workflow.fullScreen,
      stepNumber: this.currentStepIndex() + 1,
      stepCount: this.props.briefQuestions.length,
    });

    return <PAPQuestionBase {...fieldProps} data-transition-id={`brief-${fieldDefinition.name}`} />
  }

  onNextStep() {
    this.props.dispatch(submit('pap_brief'));
  }

  onPrevStep() {
    this.props.router.push({pathname: this.getPrevStepLink()});
  }

  handleSubmit(data) {
    const {
      router, projectID, briefQuestions, updateProjectRequest, setAgencySegment,
      setBannerError, stepName, updateAttachments, attachments
    } = this.props;
    let promise = null
    const briefAnswers = omit(data, ['file_upload', 'create-account', 'is_agency'])

    /* "attachments" step */
    if (stepName == 'file_upload') {
      promise = updateAttachments(projectID, attachments, data)

    } else if (stepName === 'is_agency') {
      promise = setAgencySegment(data.is_agency === 'yes');
    /* default */
    } else {
      promise = updateProjectRequest(projectID, {brief_answers: briefAnswers})
    }

    return promise.then(() => router.push(this.getNextStepLink())).catch((err) => {
      if (stepName === 'file_upload' && idx(err, _ => _.data.filename[0])) {
        setBannerError('Invalid attachment', idx(err, _ => _.data.filename[0]));
        return;
      }

      setBannerError('Error updating project', 'Please try again.');
    });
  }

  render () {
    const loadingData = this.props.submitting;
    const { handleSubmit, projectData } = this.props

    return <PAPBase
      sectionName="brief"
      extraClasses={["brief"]}
      loadingData={loadingData}
      currentStepIndex={this.currentStepIndex()}
      currentSectionStepCount={this.props.briefQuestions.length}
      onPrevStep={this.onPrevStep.bind(this)}
      onNextStep={this.onNextStep.bind(this)}
      nextEnabled={this.props.valid}
      prevEnabled={this.currentStepIndex() > 0} // FIXME: this should be enabled on step 0, but required a version of the details component with support for existing projects
      currentFeatures={projectData? projectData.features : []}
      currentAwards={projectData? projectData.award_rules : []}
      currentTips={projectData? projectData.package_tips : []}
      billables={projectData? projectData.billables:null}
      {...this.props}>
        <Form onSubmit={handleSubmit(this.handleSubmit.bind(this))}>
          <PageTransition timeout={500}>
            { this.renderWizardStep() }
          </PageTransition>
        </Form>
      </PAPBase>
  }
}

export default PAPBrief;



// WEBPACK FOOTER //
// ./pap/components/brief.jsx